import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import MovieGrid from "@/components/feature/MovieGrid";
import { categories, regionOptions, yearOptions, sortOptions } from "@/mocks/movies";
import { filterMovies, sortMovies, getGenresByType } from "@/lib/movies";
import type { CategoryItem } from "@/types/movie";

const PAGE_SIZE = 12;
const catList = categories as unknown as CategoryItem[];

export default function Category() {
  const { type = "电影" } = useParams();
  const [subType, setSubType] = useState(type);
  const [region, setRegion] = useState("全部");
  const [year, setYear] = useState("全部");
  const [genre, setGenre] = useState("全部");
  const [sort, setSort] = useState("latest");
  const [page, setPage] = useState(1);
  const [filterOpen, setFilterOpen] = useState(false);

  useEffect(() => {
    setSubType(type);
    setRegion("全部");
    setYear("全部");
    setGenre("全部");
    setSort("latest");
    setPage(1);
  }, [type]);

  useEffect(() => {
    setPage(1);
  }, [subType, region, year, genre, sort]);

  const genreOptions = useMemo(() => getGenresByType(subType), [subType]);

  const results = useMemo(
    () => sortMovies(filterMovies({ type: subType, region, year, genre }), sort),
    [subType, region, year, genre, sort],
  );

  const totalPages = Math.max(1, Math.ceil(results.length / PAGE_SIZE));
  const paged = results.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const currentCat = catList.find((c) => c.key === type);

  const resetFilters = () => {
    setRegion("全部");
    setYear("全部");
    setGenre("全部");
    setSort("latest");
    setPage(1);
  };

  const FilterGroup = ({
    label,
    options,
    value,
    onChange,
  }: {
    label: string;
    options: { key?: string; label: string }[];
    value: string;
    onChange: (v: string) => void;
  }) => (
    <div className="flex flex-col gap-2 md:flex-row md:items-start md:gap-4">
      <span className="shrink-0 pt-1 text-sm font-medium text-foreground-500 md:w-12">
        {label}
      </span>
      <div className="flex flex-wrap gap-2">
        {options.map((opt) => {
          const optValue = opt.key ?? opt.label;
          const active = value === optValue;
          return (
            <button
              key={optValue}
              type="button"
              onClick={() => onChange(optValue)}
              className={`cursor-pointer whitespace-nowrap rounded-md px-3 py-1 text-sm transition-colors ${
                active
                  ? "bg-primary-600 text-background-50"
                  : "bg-background-100 text-foreground-700 hover:bg-primary-100 hover:text-primary-700"
              }`}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );

  const pagination = () => {
    const pages: number[] = [];
    const start = Math.max(1, page - 2);
    const end = Math.min(totalPages, start + 4);
    for (let i = start; i <= end; i += 1) pages.push(i);
    return pages;
  };

  return (
    <div className="mx-auto w-full max-w-[1280px] px-4 py-6 md:px-6 md:py-8">
      {/* Breadcrumb */}
      <nav className="flex flex-wrap items-center gap-1 text-xs text-foreground-500">
        <Link to="/" className="hover:text-primary-700">首页</Link>
        <i className="ri-arrow-right-s-line" />
        <span>影视分类</span>
        <i className="ri-arrow-right-s-line" />
        <span className="text-foreground-800">{type}</span>
      </nav>

      <div className="mt-3 flex items-center gap-3">
        <h2 className="font-heading text-2xl font-extrabold text-foreground-950 md:text-3xl">
          {type}
        </h2>
        <span className="rounded-full bg-accent-100 px-2.5 py-1 text-xs font-semibold text-accent-700">
          共 {results.length} 部
        </span>
      </div>
      <p className="mt-2 max-w-3xl text-sm leading-relaxed text-foreground-500">
        {currentCat?.desc || "精选高清影视内容，支持多维度筛选与排序。"}
      </p>

      {/* Type tabs */}
      <div className="mt-5 flex flex-wrap gap-2">
        {catList.map((cat) => (
          <Link
            key={cat.key}
            to={`/category/${cat.key}`}
            className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
              cat.key === type
                ? "bg-primary-600 text-background-50"
                : "border border-background-300 text-foreground-700 hover:border-primary-300 hover:text-primary-700"
            }`}
          >
            {cat.label}
          </Link>
        ))}
      </div>

      {/* Filters */}
      <div className="mt-5 rounded-xl border border-background-200 bg-background-50 p-4">
        <button
          type="button"
          onClick={() => setFilterOpen((v) => !v)}
          className="flex w-full cursor-pointer items-center justify-between text-sm font-semibold text-foreground-800 md:hidden"
        >
          <span className="flex items-center gap-2">
            <i className="ri-filter-3-line" /> 筛选与排序
          </span>
          <i className={filterOpen ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} />
        </button>

        <div
          className={`${filterOpen ? "mt-4 flex" : "hidden"} flex-col gap-4 md:mt-0 md:flex`}
        >
          <FilterGroup label="地区" options={regionOptions.map((r) => ({ label: r }))} value={region} onChange={setRegion} />
          <FilterGroup label="年份" options={yearOptions.map((y) => ({ label: y }))} value={year} onChange={setYear} />
          <FilterGroup label="题材" options={genreOptions.map((g) => ({ label: g }))} value={genre} onChange={setGenre} />
          <FilterGroup
            label="排序"
            options={sortOptions.map((s) => ({ key: s.key, label: s.label }))}
            value={sort}
            onChange={setSort}
          />
          <div className="flex justify-end border-t border-background-200 pt-3 md:pt-2">
            <button
              type="button"
              onClick={resetFilters}
              className="flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md border border-background-300 px-3 py-1.5 text-sm text-foreground-600 transition-colors hover:border-accent-400 hover:text-accent-600"
            >
              <i className="ri-refresh-line" /> 重置筛选
            </button>
          </div>
        </div>
      </div>

      {/* Results */}
      <div className="mt-6">
        <MovieGrid movies={paged} emptyText="暂无符合筛选条件的影视，试试调整或重置筛选" />
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-8 flex flex-wrap items-center justify-center gap-2">
          <button
            type="button"
            disabled={page === 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md border border-background-300 px-3 py-1.5 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <i className="ri-arrow-left-s-line" /> 上一页
          </button>
          {pagination().map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => setPage(p)}
              className={`h-9 w-9 cursor-pointer rounded-md text-sm font-medium transition-colors ${
                p === page
                  ? "bg-primary-600 text-background-50"
                  : "border border-background-300 text-foreground-700 hover:border-primary-300 hover:text-primary-700"
              }`}
            >
              {p}
            </button>
          ))}
          <button
            type="button"
            disabled={page === totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            className="flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md border border-background-300 px-3 py-1.5 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700 disabled:cursor-not-allowed disabled:opacity-40"
          >
            下一页 <i className="ri-arrow-right-s-line" />
          </button>
        </div>
      )}
    </div>
  );
}